import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import GlassButton from './GlassButton';
import './fonts.css';
import { supabase } from './supabaseClient';

function Admin() {
  const navigate = useNavigate();
  const [state, setState] = useState({
    dhikrs: [],
    loading: true,
    error: null,
    editingId: null,
    formData: {
      name: '',
      count: 0,
      order_num: 0
    },
    incrementInputs: {},
    editingValues: {}, // For storing temporary values during editing
    enablePdf: true // Toggle for PDF generation
  });

  const setLoading = (loading) => setState(prev => ({ ...prev, loading }));
  const setError = (error) => setState(prev => ({ ...prev, error }));
  const setDhikrs = (dhikrs) => setState(prev => ({
    ...prev,
    dhikrs: typeof dhikrs === 'function' ? dhikrs(prev.dhikrs) : dhikrs
  }));
  const setEditingId = (editingId) => setState(prev => ({ ...prev, editingId }));
  const setFormData = (formData) => setState(prev => ({ ...prev, formData }));
  const setIncrementInputs = (incrementInputs) => setState(prev => ({ ...prev, incrementInputs }));
  const setEditingValues = (editingValues) => setState(prev => ({ ...prev, editingValues }));

  const { dhikrs, loading, error, editingId, formData, incrementInputs, enablePdf } = state;

  // Fetch dhikrs from Supabase
  useEffect(() => {
    fetchDhikrs();
  }, []);

  const fetchDhikrs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('dhikrs')
        .select('*')
        .order('order_num');

      if (error) {
        throw new Error(error.message);
      }

      setDhikrs(data || []);
      setError(null);
    } catch (err) {
      setError(err.message);
      // Fallback to sample data
      setDhikrs([
        { id: 1, name: "سبحان الله", count: 48066, order_num: 1 },
        { id: 2, name: "الحمدلله", count: 35240, order_num: 2 },
        { id: 3, name: "لا إله إلا الله", count: 28950, order_num: 3 },
        { id: 4, name: "الله أكبر", count: 18750, order_num: 4 },
        { id: 5, name: "أستغفر الله", count: 12300, order_num: 5 }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkIncrementFirstFour = async (direction) => {
    try {
      const targets = (dhikrs || []).slice(0, 4);
      if (targets.length === 0) return;

      const rows = targets.map((d) => {
        const raw = (incrementInputs && incrementInputs[d.id] !== undefined) ? incrementInputs[d.id] : 0;
        const delta = Math.abs(parseInt(raw) || 0);
        return {
          id: d.id,
          name: d.name,
          delta: direction * delta
        };
      });

      const hasAny = rows.some(r => r.delta !== 0);
      if (!hasAny) return;

      const updates = targets
        .map((d) => {
          const row = rows.find(r => r.id === d.id);
          const newCount = Math.max(0, d.count + (row?.delta || 0));
          return { id: d.id, newCount };
        });

      const results = await Promise.all(
        updates.map(u =>
          supabase
            .from('dhikrs')
            .update({ count: u.newCount })
            .eq('id', u.id)
            .select()
        )
      );

      const firstError = results.find(r => r.error)?.error;
      if (firstError) {
        throw new Error(firstError.message);
      }

      setDhikrs(prevDhikrs =>
        prevDhikrs.map(d => {
          const u = updates.find(x => x.id === d.id);
          return u ? { ...d, count: u.newCount } : d;
        })
      );

      localStorage.setItem('dhikrUpdated', Date.now());

      setIncrementInputs({});

      if (enablePdf) {
        navigate('/receipt', {
          state: {
            createdAt: new Date().toISOString(),
            rows: rows.map(r => ({ name: r.name, registered: r.delta }))
          }
        });
      }
    } catch (err) {
      console.error('Bulk increment error:', err);
      setError(`خطا در افزودن گروهی: ${err.message}`);
    }
  };

  const handleCreate = async () => {
    try {
      const { data, error } = await supabase
        .from('dhikrs')
        .insert({
          name: formData.name,
          count: formData.count,
          order_num: formData.order_num
        })
        .select();

      if (error) {
        throw new Error(error.message);
      }

      console.log('Created dhikr:', data);
      setFormData({ name: '', count: 0, order_num: 0 });
      fetchDhikrs();
      
      // Trigger storage event to notify main page
      localStorage.setItem('dhikrUpdated', Date.now());
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdate = async (id) => {
    try {
      console.log('Updating dhikr:', id, formData);
      
      const { data, error } = await supabase
        .from('dhikrs')
        .update({
          name: formData.name,
          count: parseInt(formData.count),
          order_num: parseInt(formData.order_num)
        })
        .eq('id', id)
        .select();

      if (error) {
        console.error('Supabase error:', error);
        throw new Error(`Failed to update dhikr: ${error.message}`);
      }

      console.log('Update successful:', data);

      // Update local state immediately
      setDhikrs(prevDhikrs => 
        prevDhikrs.map(dhikr => 
          dhikr.id === id ? { ...dhikr, ...formData } : dhikr
        )
      );
      
      setEditingId(null);
      setFormData({ name: '', count: 0, order_num: 0 });
      setError(null);
      
      // Trigger storage event to notify main page
      localStorage.setItem('dhikrUpdated', Date.now());
      
    } catch (err) {
      console.error('Update error:', err);
      setError(`خطا در به‌روزرسانی: ${err.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این ذکر مطمئن هستید؟')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('dhikrs')
        .delete()
        .eq('id', id);

      if (error) {
        throw new Error(error.message);
      }

      fetchDhikrs();
      
      // Trigger storage event to notify main page
      localStorage.setItem('dhikrUpdated', Date.now());
    } catch (err) {
      setError(err.message);
    }
  };

  const startEdit = (dhikr) => {
    setEditingId(dhikr.id);
    setFormData({
      name: dhikr.name,
      count: dhikr.count,
      order_num: dhikr.order_num
    });
  };

  const handleQuickIncrement = async (id, increment) => {
    try {
      const dhikr = dhikrs.find(d => d.id === id);
      if (!dhikr) return;

      const newCount = Math.max(0, dhikr.count + increment);
      
      const { data, error } = await supabase
        .from('dhikrs')
        .update({ count: newCount })
        .eq('id', id)
        .select();

      if (error) {
        throw new Error(error.message);
      }

      // Update local state immediately
      setDhikrs(prevDhikrs => 
        prevDhikrs.map(d => 
          d.id === id ? { ...d, count: newCount } : d
        )
      );
      
      // Trigger storage event to notify main page
      localStorage.setItem('dhikrUpdated', Date.now());
      
    } catch (err) {
      console.error('Quick increment error:', err);
      setError(`خطا در افزودن: ${err.message}`);
    }
  };

  const handleCustomIncrement = async (id, value) => {
    const increment = parseInt(value) || 0;
    if (increment <= 0) return;
    
    await handleQuickIncrement(id, increment);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData({ name: '', count: 0, order_num: 0 });
    setState(prev => ({
      ...prev,
      editingValues: {}
    }));
  };

  const updateEditingValue = (id, value) => {
    setState(prev => ({
      ...prev,
      editingValues: {
        ...prev.editingValues,
        [id]: value
      }
    }));
  };

  const saveDhikrEdit = (id) => {
    const newCount = state.editingValues[id];
    if (newCount >= 0 && newCount <= 999999) {
      handleUpdate(id, { count: newCount });
    }
  };

  if (state.loading) {
    return (
      <div style={{ 
        width: '100vw', 
        height: '100vh',
        backgroundColor: '#000000',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: 'white',
        fontFamily: 'AbarMidFaNum, Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '10px' }}>بارگذاری...</div>
          <div style={{ fontSize: '16px', opacity: '0.7' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100vw', 
      height: '100vh',
      backgroundColor: '#000000',
      position: 'relative',
      overflow: 'auto'
    }}>
      {/* Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        left: 0,
        right: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(20px)',
        padding: '20px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        zIndex: 100,
        textAlign: 'center'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '15px',
          marginBottom: '10px'
        }}>
          <h1 style={{ 
            fontSize: '28px', 
            margin: '0',
            color: 'white',
            fontFamily: 'AbarMidFaNum, Arial, sans-serif',
            fontWeight: 'bold'
          }}>
            مدیریت اذکار
          </h1>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: 'white',
            fontFamily: 'AbarMidFaNum, Arial, sans-serif',
            fontSize: '14px',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={enablePdf}
              onChange={(e) => setState(prev => ({ ...prev, enablePdf: e.target.checked }))}
              style={{
                width: '18px',
                height: '18px',
                cursor: 'pointer'
              }}
            />
            دریافت PDF
          </label>
        </div>
        <p style={{ 
          fontSize: '14px', 
          margin: '5px 0 0 0',
          color: 'rgba(255,255,255,0.8)',
          fontFamily: 'AbarMidFaNum, Arial, sans-serif'
        }}>
          Dhikr Management Panel
        </p>
        <Link to="/" style={{ textDecoration: 'none', marginTop: '10px', display: 'inline-block', marginLeft: '10px' }}>
          <GlassButton
            style={{
              padding: '8px 16px',
              fontSize: '12px',
              background: 'rgba(255, 152, 0, 0.2)',
              border: '1px solid rgba(255, 152, 0, 0.5)',
              fontFamily: 'AbarMidFaNum, Arial, sans-serif'
            }}
          >
            🏠 بازگشت به صفحه اصلی
          </GlassButton>
        </Link>
        <Link to="/quran-admin" style={{ textDecoration: 'none', marginTop: '10px', display: 'inline-block' }}>
          <GlassButton
            style={{
              padding: '8px 16px',
              fontSize: '12px',
              background: 'rgba(76, 175, 80, 0.2)',
              border: '1px solid rgba(76, 175, 80, 0.5)',
              fontFamily: 'AbarMidFaNum, Arial, sans-serif'
            }}
          >
            مدیریت قرآن
          </GlassButton>
        </Link>
        <Link to="/amal-admin" style={{ textDecoration: 'none', marginTop: '10px', display: 'inline-block' }}>
          <GlassButton
            style={{
              padding: '8px 16px',
              fontSize: '12px',
              background: 'rgba(0, 191, 255, 0.2)',
              border: '1px solid rgba(0, 191, 255, 0.5)',
              fontFamily: 'AbarMidFaNum, Arial, sans-serif'
            }}
          >
            مدیریت اعمال
          </GlassButton>
        </Link>
      </div>

      {/* Main Content */}
      <div style={{
        padding: '20px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Error Message */}
        {error && (
          <div style={{
            background: 'rgba(255, 0, 0, 0.1)',
            border: '1px solid rgba(255, 0, 0, 0.3)',
            borderRadius: '10px',
            padding: '15px',
            marginBottom: '20px',
            color: '#ff6b6b',
            textAlign: 'center',
            fontSize: '14px',
            fontFamily: 'AbarMidFaNum, Arial, sans-serif'
          }}>
            خطا: {error}
          </div>
        )}

        {/* Dhikrs List */}
        <div style={{
          display: 'grid',
          gap: '15px'
        }}>
          {dhikrs.map((dhikr) => (
            <div
              key={dhikr.id}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(20px)',
                borderRadius: '15px',
                padding: '20px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'transform 0.3s ease'
              }}
            >
              {editingId === dhikr.id ? (
                // Edit Mode
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr auto auto',
                  gap: '10px',
                  alignItems: 'center'
                }}>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    style={{
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                      direction: 'ltr',
                      textAlign: 'left'
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <GlassButton
                      onClick={() => handleUpdate(dhikr.id, { count: Math.max(0, (state.editingValues[dhikr.id] !== undefined ? state.editingValues[dhikr.id] : formData.count) - 1) })}
                      style={{
                        padding: '6px 10px',
                        fontSize: '14px',
                        background: 'rgba(255, 152, 0, 0.2)',
                        border: '1px solid rgba(255, 152, 0, 0.5)',
                        fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                        minWidth: '30px',
                        borderRadius: '6px'
                      }}
                    >
                      -
                    </GlassButton>
                    <input
                      type="number"
                      value={state.editingValues[dhikr.id] !== undefined ? state.editingValues[dhikr.id] : formData.count}
                      onChange={(e) => {
                        const newCount = parseInt(e.target.value) || 0;
                        if (newCount >= 0 && newCount <= 999999) {
                          setFormData({...formData, count: newCount });
                        }
                      }}
                      style={{
                        width: '80px',
                        padding: '6px 8px',
                        borderRadius: '6px',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        fontSize: '14px',
                        fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                        textAlign: 'center'
                      }}
                    />
                    <div style={{
                      color: '#4CAF50',
                      fontSize: '12px',
                      fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                      minWidth: '40px',
                      textAlign: 'center'
                    }}>
                      فعلی: {dhikr.count.toLocaleString('fa-IR')}
                    </div>
                    <GlassButton
                      onClick={() => {
                        const newValue = state.editingValues[dhikr.id] !== undefined ? state.editingValues[dhikr.id] : formData.count;
                        handleUpdate(dhikr.id, { count: newValue });
                        setFormData({...formData, count: newValue });
                        setState(prev => ({
                          ...prev,
                          editingValues: {
                            ...prev.editingValues,
                            [dhikr.id]: undefined
                          }
                        }));
                      }}
                      style={{
                        padding: '6px 10px',
                        fontSize: '14px',
                        background: 'rgba(76, 175, 80, 0.2)',
                        border: '1px solid rgba(76, 175, 80, 0.5)',
                        fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                        minWidth: '30px',
                        borderRadius: '6px'
                      }}
                    >
                      +
                    </GlassButton>
                  </div>
                  <GlassButton
                    onClick={() => handleUpdate(dhikr.id)}
                    style={{
                      padding: '10px 15px',
                      fontSize: '12px',
                      background: 'rgba(33, 150, 243, 0.2)',
                      border: '1px solid rgba(33, 150, 243, 0.5)',
                      fontFamily: 'AbarMidFaNum, Arial, sans-serif'
                    }}
                  >
                    💾 ذخیره
                  </GlassButton>
                  <GlassButton
                    onClick={cancelEdit}
                    style={{
                      padding: '10px 15px',
                      fontSize: '12px',
                      background: 'rgba(255, 152, 0, 0.2)',
                      border: '1px solid rgba(255, 152, 0, 0.5)',
                      fontFamily: 'AbarMidFaNum, Arial, sans-serif'
                    }}
                  >
                    ❌ لغو
                  </GlassButton>
                </div>
              ) : (
                // View Mode
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr auto auto auto',
                  gap: '15px',
                  alignItems: 'center'
                }}>
                  <div style={{
                    fontSize: '18px',
                    color: 'white',
                    fontFamily: 'Al-QuranAlKareem, AbarMidFaNum, Arial, sans-serif',
                    fontWeight: 'bold',
                    direction: 'ltr',
                    textAlign: 'left'
                  }}>
                    {dhikr.name}
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      color: '#4CAF50',
                      fontSize: '12px',
                      fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                      minWidth: '110px',
                      textAlign: 'center'
                    }}>
                      فعلی: {dhikr.count.toLocaleString('fa-IR')}
                    </div>

                    <GlassButton
                      onClick={() => {
                        handleBulkIncrementFirstFour(-1);
                      }}
                      style={{
                        padding: '6px 10px',
                        fontSize: '14px',
                        background: 'rgba(255, 152, 0, 0.2)',
                        border: '1px solid rgba(255, 152, 0, 0.5)',
                        fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                        minWidth: '30px',
                        borderRadius: '6px'
                      }}
                    >
                      -
                    </GlassButton>

                    <input
                      type="number"
                      value={(incrementInputs && incrementInputs[dhikr.id] !== undefined) ? incrementInputs[dhikr.id] : 0}
                      onChange={(e) => {
                        const delta = parseInt(e.target.value) || 0;
                        setIncrementInputs({
                          ...incrementInputs,
                          [dhikr.id]: delta
                        });
                      }}
                      style={{
                        width: '80px',
                        padding: '6px 8px',
                        borderRadius: '6px',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        fontSize: '14px',
                        fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                        textAlign: 'center'
                      }}
                    />

                    <GlassButton
                      onClick={() => {
                        handleBulkIncrementFirstFour(1);
                      }}
                      style={{
                        padding: '6px 10px',
                        fontSize: '14px',
                        background: 'rgba(76, 175, 80, 0.2)',
                        border: '1px solid rgba(76, 175, 80, 0.5)',
                        fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                        minWidth: '30px',
                        borderRadius: '6px'
                      }}
                    >
                      +
                    </GlassButton>
                  </div>
                  <GlassButton
                    onClick={() => startEdit(dhikr)}
                    style={{
                      padding: '8px 12px',
                      fontSize: '12px',
                      background: 'rgba(33, 150, 243, 0.2)',
                      border: '1px solid rgba(33, 150, 243, 0.5)',
                      fontFamily: 'AbarMidFaNum, Arial, sans-serif'
                    }}
                  >
                    ✏️
                  </GlassButton>
                  <GlassButton
                    onClick={() => handleDelete(dhikr.id)}
                    style={{
                      padding: '8px 12px',
                      fontSize: '12px',
                      background: 'rgba(244, 67, 54, 0.2)',
                      border: '1px solid rgba(244, 67, 54, 0.5)',
                      fontFamily: 'AbarMidFaNum, Arial, sans-serif'
                    }}
                  >
                    🗑️
                  </GlassButton>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Refresh Button */}
        <div style={{ textAlign: 'center', marginTop: '30px' }}>
          <GlassButton
            onClick={fetchDhikrs}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              background: 'rgba(156, 39, 176, 0.2)',
              border: '1px solid rgba(156, 39, 176, 0.5)',
              fontFamily: 'AbarMidFaNum, Arial, sans-serif'
            }}
          >
            🔄 تازه‌سازی لیست
          </GlassButton>
        </div>
      </div>
    </div>
  );
}

export default Admin;
